import { useTranslation } from "@redotech/redo-i18n";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import Logo from "@redotech/redo-icons/redo-logo-cutout.svg";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback } from "react";
import { ExitOnboardingButton } from "../ui/exit-onboarding-button";
import * as styles from "./landing-header.module.css";
import { SetupLocaleSelector } from "./setup-locale-selector";

interface LandingHeaderProps {
  email?: string;
  exitText?: string;
  logoUrl?: string;
  onExit?: () => void;
  showAskRedo?: boolean;
  storeName?: string;
}

export const LandingHeader = memo(function LandingHeader({
  email,
  exitText,
  onExit,
  showAskRedo = false,
  storeName: storeNameProp,
  logoUrl,
}: LandingHeaderProps) {
  const { t } = useTranslation();
  const storeName = storeNameProp ?? t("My Store");
  const exitLabel = exitText ?? t("Exit to dashboard");
  const initials = getInitials(storeName);
  const avatarContent = logoUrl
    ? {
        type: "image" as const,
        imageUrl: logoUrl,
        altText: storeName,
        backup: { type: "text" as const, text: initials },
      }
    : { type: "text" as const, text: initials };

  return (
    <header className={styles.header}>
      <Flex
        align="center"
        className={styles.headerContent}
        justify="space-between"
      >
        <Flex align="center" className={styles.brand} gap="lg">
          <Logo className={styles.logo} />
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            {t("Setup")}
          </Text>
        </Flex>
        <Flex align="center" className={styles.actions} gap="3xl">
          {onExit ? (
            <RedoButton
              hierarchy="secondary"
              onClick={onExit}
              size="sm"
              text={exitLabel}
            />
          ) : (
            <ExitOnboardingButton
              renderAsArbiterButton
              skipPrompt
              text={exitLabel}
            />
          )}
          {showAskRedo && <AskRedoButton />}
          <SetupLocaleSelector />
          <Flex align="center" gap="md">
            <RedoAvatar content={avatarContent} size="sm" />
            <Flex className={styles.identity} dir="column" gap="none">
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {storeName}
              </Text>
              {email && (
                <Text fontSize="xxs" textColor="secondary">
                  {email}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </header>
  );
});

const AskRedoButton = memo(function AskRedoButton() {
  const { t } = useTranslation();
  const { toggle } = useAiSlidePanel();
  const handleClick = useCallback(() => toggle(), [toggle]);

  return (
    <RedoButton
      hierarchy="secondary"
      IconLeading={AiStarIcon}
      onClick={handleClick}
      size="sm"
      text={t("Ask Redo")}
    />
  );
});

function getInitials(storeName: string): string {
  return storeName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
}
